import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Spinner } from 'react-bootstrap';
import { Sparkles, Car, RefreshCw, ChevronDown, ChevronUp, Users } from 'lucide-react';
import { getRecommendedDrivers } from '../api';

const EquityRadarWidget = ({ baseId = 1, onSelectDriver }) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({
        standard_recommendations: [],
        van_recommendations: [],
        dispatcher: ''
    });
    const [isCollapsed, setIsCollapsed] = useState(() => {
        return localStorage.getItem('emigo_equity_radar_collapsed') === 'true';
    });
    const [lastUpdated, setLastUpdated] = useState(null);

    const loadRecommendations = useCallback(async (force = false) => {
        try {
            setLoading(true);
            const res = await getRecommendedDrivers({
                base_id: baseId,
                force_refresh: force
            });
            if (res) {
                setData(res);
                setLastUpdated(new Date());
            }
        } catch (e) {
            console.error("Error al cargar recomendaciones de equidad:", e);
        } finally {
            setLoading(false);
        }
    }, [baseId]);

    useEffect(() => {
        loadRecommendations(false);
    }, [loadRecommendations]);

    const toggleCollapse = () => {
        setIsCollapsed(prev => {
            const next = !prev;
            localStorage.setItem('emigo_equity_radar_collapsed', String(next));
            return next;
        });
    };

    const hasRecommendations = (data.standard_recommendations?.length > 0) || (data.van_recommendations?.length > 0);

    // Si la base no es Santiago (1) y no hay datos, no estorbar
    if (baseId && String(baseId) !== '1') {
        return null;
    }

    return (
        <div 
            className="mb-2 rounded shadow-sm overflow-hidden" 
            style={{
                backgroundColor: '#0b1120',
                border: '1px solid #1e293b',
                color: '#f8fafc'
            }}
        >
            {/* Header ultra compacto */}
            <div 
                className="d-flex align-items-center justify-content-between px-3 py-1.5"
                style={{
                    backgroundColor: '#0f172a',
                    borderBottom: isCollapsed ? 'none' : '1px solid #1e293b'
                }}
            >
                <div className="d-flex align-items-center gap-2">
                    <div 
                        className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{ width: '22px', height: '22px', backgroundColor: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8' }}
                    >
                        <Sparkles size={12} />
                    </div>
                    <span style={{ fontWeight: 700, fontSize: '12px', color: '#ffffff' }}>
                        Radar de Equidad — Santiago
                    </span>
                    <span 
                        className="px-1.5 py-0.5 rounded fw-bold"
                        style={{ fontSize: '9px', backgroundColor: '#38bdf8', color: '#090d16', letterSpacing: '0.4px' }}
                    >
                        PRO
                    </span>
                    <span className="d-none d-lg-inline text-truncate" style={{ fontSize: '11px', color: '#94a3b8', maxWidth: '380px' }}>
                        Conductores con menor carga mensual sugeridos para equilibrar flota
                    </span>
                </div>

                <div className="d-flex align-items-center gap-2">
                    {lastUpdated && (
                        <span className="d-none d-sm-inline" style={{ fontSize: '10px', color: '#64748b' }}>
                            {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                    )}
                    <button 
                        type="button"
                        onClick={() => loadRecommendations(true)}
                        disabled={loading}
                        className="btn btn-sm d-flex align-items-center gap-1 py-0.5 px-2"
                        style={{
                            fontSize: '11px',
                            backgroundColor: '#1e293b',
                            border: '1px solid #334155',
                            color: '#e2e8f0',
                            borderRadius: '4px'
                        }}
                    >
                        <RefreshCw size={11} className={loading ? "animate-spin" : ""} />
                        <span>{loading ? 'Cargando...' : 'Refrescar'}</span>
                    </button>
                    <button 
                        type="button"
                        onClick={toggleCollapse}
                        className="btn btn-sm p-0 d-flex align-items-center justify-content-center"
                        style={{
                            width: '22px',
                            height: '22px',
                            backgroundColor: 'transparent',
                            border: 'none',
                            color: '#94a3b8'
                        }}
                        title={isCollapsed ? "Expandir radar" : "Minimizar radar"}
                    >
                        {isCollapsed ? <ChevronDown size={15} /> : <ChevronUp size={15} />}
                    </button>
                </div>
            </div>

            {/* Contenido compacto */}
            {!isCollapsed && (
                <div className="p-2" style={{ backgroundColor: '#0b1120' }}>
                    {loading && !hasRecommendations ? (
                        <div className="text-center py-2" style={{ color: '#94a3b8', fontSize: '12px' }}>
                            <Spinner animation="border" size="sm" className="me-2" style={{ width: '12px', height: '12px' }} />
                            <span>Analizando carga de conductores...</span>
                        </div>
                    ) : (
                        <Row className="g-2">
                            {/* Standard */}
                            <Col md={6}>
                                <div 
                                    className="d-flex align-items-center justify-content-between px-1 mb-1"
                                    style={{ fontSize: '11px', fontWeight: 600, color: '#f59e0b' }}
                                >
                                    <div className="d-flex align-items-center gap-1">
                                        <Car size={12} />
                                        <span>SUGERIDOS SEDÁN / STANDARD</span>
                                    </div>
                                    <span style={{ fontSize: '10px', color: '#64748b' }}>Menor carga 30d</span>
                                </div>
                                <div className="d-flex flex-column gap-1">
                                    {(!data.standard_recommendations || data.standard_recommendations.length === 0) ? (
                                        <div className="p-1.5 rounded text-center" style={{ backgroundColor: '#131c2e', color: '#64748b', fontSize: '11px' }}>
                                            Sin conductores libres en turno
                                        </div>
                                    ) : (
                                        data.standard_recommendations.slice(0, 3).map((d, idx) => (
                                            <div 
                                                key={d.driver_id}
                                                onClick={() => onSelectDriver && onSelectDriver(d)}
                                                className="d-flex align-items-center justify-content-between px-2 py-1 rounded"
                                                style={{
                                                    backgroundColor: '#131c2e',
                                                    border: '1px solid #1e293b',
                                                    cursor: onSelectDriver ? 'pointer' : 'default',
                                                    transition: 'all 0.15s ease'
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1e293b'}
                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#131c2e'}
                                            >
                                                <div className="d-flex align-items-center gap-2 min-w-0">
                                                    <span 
                                                        className="badge rounded-pill"
                                                        style={{ backgroundColor: '#1e293b', color: '#f59e0b', fontSize: '10px', padding: '2px 5px' }}
                                                    >
                                                        #{idx + 1}
                                                    </span>
                                                    <span 
                                                        className="text-truncate"
                                                        style={{ color: '#ffffff', fontWeight: 600, fontSize: '12px', maxWidth: '140px' }}
                                                        title={d.driver_name}
                                                    >
                                                        {d.driver_name}
                                                    </span>
                                                    <span 
                                                        className="badge"
                                                        style={{ backgroundColor: '#0f172a', color: '#94a3b8', border: '1px solid #334155', fontSize: '10px', padding: '2px 5px' }}
                                                    >
                                                        Móvil {d.vehicle_code}
                                                    </span>
                                                    <span style={{ fontSize: '10px', color: '#64748b' }}>
                                                        {d.shift_today || 'En turno'}
                                                    </span>
                                                </div>

                                                <div className="d-flex align-items-center gap-2">
                                                    <span style={{ fontSize: '11px', color: '#38bdf8', fontWeight: 600 }}>
                                                        {d.trips_last_30d} v. (30d)
                                                    </span>
                                                    <span 
                                                        className="badge"
                                                        style={{
                                                            backgroundColor: d.assigned_by_me_last_30d === 0 ? 'rgba(16, 185, 129, 0.2)' : 'rgba(56, 189, 248, 0.15)',
                                                            color: d.assigned_by_me_last_30d === 0 ? '#34d399' : '#38bdf8',
                                                            fontSize: '9px',
                                                            padding: '2px 6px'
                                                        }}
                                                    >
                                                        {d.assigned_by_me_last_30d === 0 ? "0 tuyos" : "Sugerido"}
                                                    </span>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </Col>

                            {/* VAN */}
                            <Col md={6}>
                                <div 
                                    className="d-flex align-items-center justify-content-between px-1 mb-1"
                                    style={{ fontSize: '11px', fontWeight: 600, color: '#38bdf8' }}
                                >
                                    <div className="d-flex align-items-center gap-1">
                                        <Users size={12} />
                                        <span>SUGERIDOS VAN (PEOPLE CARRIER)</span>
                                    </div>
                                    <span style={{ fontSize: '10px', color: '#64748b' }}>Menor carga 30d</span>
                                </div>
                                <div className="d-flex flex-column gap-1">
                                    {(!data.van_recommendations || data.van_recommendations.length === 0) ? (
                                        <div className="p-1.5 rounded text-center" style={{ backgroundColor: '#131c2e', color: '#64748b', fontSize: '11px' }}>
                                            Sin conductores libres en turno
                                        </div>
                                    ) : (
                                        data.van_recommendations.slice(0, 3).map((d, idx) => (
                                            <div 
                                                key={d.driver_id}
                                                onClick={() => onSelectDriver && onSelectDriver(d)}
                                                className="d-flex align-items-center justify-content-between px-2 py-1 rounded"
                                                style={{
                                                    backgroundColor: '#131c2e',
                                                    border: '1px solid #1e293b',
                                                    cursor: onSelectDriver ? 'pointer' : 'default',
                                                    transition: 'all 0.15s ease'
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1e293b'}
                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#131c2e'}
                                            >
                                                <div className="d-flex align-items-center gap-2 min-w-0">
                                                    <span 
                                                        className="badge rounded-pill"
                                                        style={{ backgroundColor: '#1e293b', color: '#38bdf8', fontSize: '10px', padding: '2px 5px' }}
                                                    >
                                                        #{idx + 1}
                                                    </span>
                                                    <span 
                                                        className="text-truncate"
                                                        style={{ color: '#ffffff', fontWeight: 600, fontSize: '12px', maxWidth: '140px' }}
                                                        title={d.driver_name}
                                                    >
                                                        {d.driver_name}
                                                    </span>
                                                    <span 
                                                        className="badge"
                                                        style={{ backgroundColor: '#0f172a', color: '#94a3b8', border: '1px solid #334155', fontSize: '10px', padding: '2px 5px' }}
                                                    >
                                                        Móvil {d.vehicle_code}
                                                    </span>
                                                    <span style={{ fontSize: '10px', color: '#64748b' }}>
                                                        {d.shift_today || 'En turno'}
                                                    </span>
                                                </div>

                                                <div className="d-flex align-items-center gap-2">
                                                    <span style={{ fontSize: '11px', color: '#38bdf8', fontWeight: 600 }}>
                                                        {d.trips_last_30d} v. (30d)
                                                    </span>
                                                    <span 
                                                        className="badge"
                                                        style={{
                                                            backgroundColor: d.assigned_by_me_last_30d === 0 ? 'rgba(16, 185, 129, 0.2)' : 'rgba(56, 189, 248, 0.15)',
                                                            color: d.assigned_by_me_last_30d === 0 ? '#34d399' : '#38bdf8',
                                                            fontSize: '9px',
                                                            padding: '2px 6px'
                                                        }}
                                                    >
                                                        {d.assigned_by_me_last_30d === 0 ? "0 tuyos" : "Sugerido"}
                                                    </span>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </Col>
                        </Row>
                    )}
                </div>
            )}
        </div>
    );
};

export default EquityRadarWidget;
